import { useState, useEffect } from "react";
import axios from "axios";
import { Plus, Trash2, Key, User, Eye, EyeOff, Shield } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const AdminManagement = () => {
  const token = localStorage.getItem("token");
  const currentUser = localStorage.getItem("username");
  const [admins, setAdmins] = useState([]);
  const [loading, setLoading] = useState(false);
  
  // Create admin state
  const [createOpen, setCreateOpen] = useState(false);
  const [newUsername, setNewUsername] = useState("");
  const [newPassword, setNewPassword] = useState("");
  const [showNewPassword, setShowNewPassword] = useState(false);
  
  // Update password state
  const [updateOpen, setUpdateOpen] = useState(false);
  const [selectedAdmin, setSelectedAdmin] = useState("");
  const [updatePassword, setUpdatePassword] = useState("");
  const [showUpdatePassword, setShowUpdatePassword] = useState(false);

  const axiosAuth = axios.create({
    headers: { Authorization: `Bearer ${token}` }
  });

  useEffect(() => {
    fetchAdmins();
  }, []);

  const fetchAdmins = async () => {
    try {
      const res = await axiosAuth.get(`${API}/auth/admins`);
      setAdmins(res.data);
    } catch (err) {
      toast.error("Failed to fetch admins");
    }
  };

  const handleCreateAdmin = async (e) => {
    e.preventDefault();
    if (!newUsername.trim() || !newPassword.trim()) {
      toast.error("Username and password are required");
      return;
    }
    if (newPassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    try {
      await axiosAuth.post(`${API}/auth/admins`, {
        username: newUsername,
        password: newPassword
      });
      toast.success(`Admin '${newUsername}' created successfully!`);
      setNewUsername("");
      setNewPassword("");
      setCreateOpen(false);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to create admin");
    } finally {
      setLoading(false);
    }
  };

  const handleUpdatePassword = async (e) => {
    e.preventDefault();
    if (!updatePassword.trim()) {
      toast.error("New password is required");
      return;
    }
    if (updatePassword.length < 6) {
      toast.error("Password must be at least 6 characters");
      return;
    }
    
    setLoading(true);
    try {
      await axiosAuth.put(`${API}/auth/admins/password`, {
        username: selectedAdmin,
        new_password: updatePassword
      });
      toast.success(`Password updated for '${selectedAdmin}'`);
      setUpdatePassword("");
      setSelectedAdmin("");
      setUpdateOpen(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  const handleDeleteAdmin = async (username) => {
    if (username === currentUser) {
      toast.error("Cannot delete your own account");
      return;
    }
    
    if (!window.confirm(`Are you sure you want to delete admin '${username}'?`)) {
      return;
    }
    
    try {
      await axiosAuth.delete(`${API}/auth/admins/${username}`);
      toast.success(`Admin '${username}' deleted`);
      fetchAdmins();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Failed to delete admin");
    }
  };

  const openUpdateDialog = (username) => {
    setSelectedAdmin(username);
    setUpdatePassword("");
    setShowUpdatePassword(false);
    setUpdateOpen(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <p className="text-slate-500 dark:text-slate-400 text-sm">
          {admins.length} admin account{admins.length !== 1 ? "s" : ""}
        </p>
        
        {/* Create Admin Dialog */}
        <Dialog open={createOpen} onOpenChange={setCreateOpen}>
          <DialogTrigger asChild>
            <Button size="sm" className="bg-amber-500 hover:bg-amber-600 text-slate-900">
              <Plus className="w-4 h-4 mr-1" /> Add Admin
            </Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Shield className="w-5 h-5 text-amber-500" />
                Create New Admin
              </DialogTitle>
            </DialogHeader>
            <form onSubmit={handleCreateAdmin} className="space-y-4 mt-4">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest">Username</Label>
                <Input
                  value={newUsername}
                  onChange={(e) => setNewUsername(e.target.value)}
                  placeholder="Enter username"
                  data-testid="new-admin-username"
                />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-widest">Password</Label>
                <div className="relative">
                  <Input
                    type={showNewPassword ? "text" : "password"}
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter password (min 6 chars)"
                    data-testid="new-admin-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowNewPassword(!showNewPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  >
                    {showNewPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                  </button>
                </div>
              </div>
              <Button
                type="submit"
                disabled={loading}
                className="w-full bg-amber-500 hover:bg-amber-600 text-slate-900 font-bold"
                data-testid="create-admin-btn"
              >
                {loading ? "Creating..." : "Create Admin"}
              </Button>
            </form>
          </DialogContent>
        </Dialog>
      </div>

      {/* Admin List */}
      <div className="space-y-2">
        {admins.map((admin) => (
          <div
            key={admin.id || admin.username}
            className="flex items-center justify-between p-3 bg-slate-50 dark:bg-slate-800 rounded-lg border border-slate-200 dark:border-slate-700"
          >
            <div className="flex items-center gap-3">
              <div className="w-8 h-8 bg-amber-500/20 rounded-full flex items-center justify-center">
                <User className="w-4 h-4 text-amber-600" />
              </div>
              <div>
                <p className="font-medium text-slate-900 dark:text-white">{admin.username}</p>
                {admin.username === currentUser && (
                  <span className="text-xs text-amber-600 dark:text-amber-400">(You)</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-2">
              <Button
                variant="ghost"
                size="sm"
                onClick={() => openUpdateDialog(admin.username)}
                className="text-blue-600 hover:text-blue-700 hover:bg-blue-50"
                data-testid={`update-pw-${admin.username}`}
              >
                <Key className="w-4 h-4 mr-1" /> Change Password
              </Button>
              {admin.username !== currentUser && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => handleDeleteAdmin(admin.username)}
                  className="text-red-600 hover:text-red-700 hover:bg-red-50"
                  data-testid={`delete-admin-${admin.username}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Update Password Dialog */}
      <Dialog open={updateOpen} onOpenChange={setUpdateOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Key className="w-5 h-5 text-blue-500" />
              Change Password for '{selectedAdmin}'
            </DialogTitle>
          </DialogHeader>
          <form onSubmit={handleUpdatePassword} className="space-y-4 mt-4">
            <div className="space-y-2">
              <Label className="text-xs uppercase tracking-widest">New Password</Label>
              <div className="relative">
                <Input
                  type={showUpdatePassword ? "text" : "password"}
                  value={updatePassword}
                  onChange={(e) => setUpdatePassword(e.target.value)}
                  placeholder="Enter new password (min 6 chars)"
                  data-testid="update-admin-password"
                />
                <button
                  type="button"
                  onClick={() => setShowUpdatePassword(!showUpdatePassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                >
                  {showUpdatePassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
            </div>
            <Button
              type="submit"
              disabled={loading}
              className="w-full bg-blue-500 hover:bg-blue-600 text-white font-bold"
              data-testid="update-password-btn"
            >
              {loading ? "Updating..." : "Update Password"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default AdminManagement;
